package navigation.home.clients.allclients

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.type
import androidx.compose.ui.unit.dp
import cafe.adriel.voyager.koin.koinScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.essntl.components.sidepanel.SidePanel
import com.essntl.components.sidepanel.SizeConstraint
import com.essntl.components.sidepanel.Splitter
import com.essntl.components.sidepanel.rememberSidePanelState
import com.essntl.features.client.ui.addeditclient.AddEditClientEvent
import com.essntl.features.client.ui.addeditclient.AddEditClientNavigation
import com.essntl.features.client.ui.addeditclient.AddEditClientScreen
import com.essntl.features.client.ui.allclients.AllClientsEvent
import com.essntl.features.client.ui.allclients.AllClientsNavigation
import com.essntl.features.client.ui.allclients.AllClientsScreen
import essntl.key.LocalKeyEventManager
import com.essntl.uikit.components.iconbutton.outline.EsMediumOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.link.NavLinkState
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import kotlinx.coroutines.flow.collectLatest
import navigation.home.clients.addeditclient.AddEditClientScreenModel
import navigation.home.edititinerary.EditItineraryScreenNav
import navigation.home.proposal.addeditproposal.AddEditProposalScreenNav
import navigation.utils.NavLinkScreen

object AllClientsScreenNav : NavLinkScreen {
    override val defaultTitle: String = "Travellers"

    override var navLinkState: NavLinkState by mutableStateOf(
        NavLinkState(
            title = defaultTitle,
            iconRes = EsIcons.Clients,
        ),
    )

    @Composable
    override fun Content() {
        val allClientsScreenModel = koinScreenModel<AllClientsScreenModel>()
        val allClientsState by allClientsScreenModel.state.collectAsState()

        val addEditClientScreenModel = koinScreenModel<AddEditClientScreenModel>()
        val addEditClientState by addEditClientScreenModel.state.collectAsState()

        val navigator = LocalNavigator.currentOrThrow

        val sidePanelState =
            rememberSidePanelState(
                sidePanelVisible = false,
                panelSizeConstraint =
                    SizeConstraint(
                        minSize = 300.dp,
                        maxSize = 600.dp,
                    ),
            )

        LaunchedEffect(Unit) {
            navLinkState = navLinkState.copy(
                onRefresh = {
                    allClientsScreenModel.onEvent(AllClientsEvent.LoadClients(refresh = true))
                    sidePanelState.sidePanelOpened = false
                },
            )
        }

        val keyEventManager = LocalKeyEventManager.current

        LaunchedEffect(keyEventManager) {
            keyEventManager.events.collectLatest {
                if (it.key == Key.H && it.type == KeyEventType.KeyUp) {
                    sidePanelState.sidePanelOpened = false
                }
            }
        }

        LaunchedEffect(sidePanelState.sidePanelOpened) {
            if (!sidePanelState.sidePanelOpened) {
                addEditClientScreenModel.onEvent(AddEditClientEvent.ClearClientState)
            }
        }

        LaunchedEffect(addEditClientState.addEditClient) {
            if (addEditClientState.addEditClient.isSuccess) {
                allClientsScreenModel.onEvent(
                    AllClientsEvent.LoadClients(
                        addEditClientState.addEditClient.data,
                    ),
                )
                addEditClientScreenModel.onEvent(AddEditClientEvent.ClearSuccessState)
            }
        }

        SidePanel(
            sidePanelState = sidePanelState,
            isHorizontal = true,
            sidePanel = {
                Column(
                    modifier =
                        Modifier
                            .fillMaxHeight()
                            .padding(end = 1.5f.spacing)
                            .clip(RoundedCornerShape(EsConstants.SectionBorderRadius)),
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.SpaceBetween,
                        modifier =
                            Modifier
                                .background(
                                    color = MaterialTheme.esColorScheme.bgSecondary,
                                    shape = RoundedCornerShape(EsConstants.RoundedCornerSize),
                                )
                                .clip(RoundedCornerShape(EsConstants.RoundedCornerSize))
                                .padding(2.5f.spacing)
                                .fillMaxWidth(),
                    ) {
                        val isEdit by remember(addEditClientState.client.value.id) {
                            mutableStateOf(
                                addEditClientState.client.value.id.isNotBlank(),
                            )
                        }

                        EsMediumOutlineIconButton(
                            onClick = {
                                sidePanelState.sidePanelOpened = false
                            },
                            iconRes = EsIcons.ArrowRight,
                        )

                        if (isEdit) {
                            SelectionContainer {
                                Text(
                                    text =
                                        addEditClientState.client.value.firstName + " " +
                                            addEditClientState.client.value.lastName,
                                    color = MaterialTheme.esColorScheme.textPrimary,
                                    style = MaterialTheme.esTypography.h3Bold24,
                                )
                            }
                        } else {
                            Text(
                                text = "New client",
                                color = MaterialTheme.esColorScheme.textPrimary,
                                style = MaterialTheme.esTypography.h3Bold24,
                            )
                        }
                    }

                    AddEditClientScreen(
                        state = addEditClientState,
                        onEvent = addEditClientScreenModel::onEvent,
                        validation = addEditClientScreenModel.validation,
                        onNavigation = {
                            when (it) {
                                is AddEditClientNavigation.EditProposal -> {
                                    navigator.push(
                                        AddEditProposalScreenNav(
                                            proposalId = it.proposalId,
                                        ),
                                    )
                                }

                                is AddEditClientNavigation.EditItinerary -> {
                                    navigator.push(
                                        EditItineraryScreenNav(
                                            itineraryId = it.itineraryId,
                                        ),
                                    )
                                }
                            }
                        },
                    )
                }
            },
            splitter =
                Splitter(
                    measuredPart = {
                        Box(
                            modifier = Modifier.width(1.spacing).fillMaxHeight(),
                        )
                    },
                ),
            modifier = Modifier.fillMaxSize(),
        ) {
            AllClientsScreen(
                state = allClientsState,
                addEditClientState = addEditClientState,
                onEvent = {
                    allClientsScreenModel.onEvent(it)
                },
                onNavigation = {
                    when (it) {
                        is AllClientsNavigation.CloseSidePanel -> {
                            addEditClientScreenModel.onEvent(AddEditClientEvent.ClearSuccessState)
                            sidePanelState.sidePanelOpened = false
                        }

                        is AllClientsNavigation.AddClient -> {
                            addEditClientScreenModel.onEvent(AddEditClientEvent.ClearClientState)
                            sidePanelState.sidePanelOpened = true
                        }

                        is AllClientsNavigation.EditClient -> {
                            if (
                                sidePanelState.sidePanelOpened &&
                                addEditClientState.getClient.data?.id == it.clientId
                            ) {
                                return@AllClientsScreen
                            }

                            addEditClientScreenModel.onEvent(AddEditClientEvent.ClearClientState)
                            addEditClientScreenModel.onEvent(AddEditClientEvent.GetClient(it.clientId))
                            sidePanelState.sidePanelOpened = true
                        }
                    }
                },
            )
        }
    }
}